/**
 * Runtime level: colliders + render list built from validated LevelData.
 * The engine consumes exactly what the editor produces — no separate
 * hard-coded gameplay representation (docs/ARCHITECTURE.md hard rules).
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK, SPEED } from '../physics/constants';
import { StaticGrid, type Aabb } from '../collision/grid';
import { triggerFromObject, type TriggerDef } from '../triggers/triggers';

export type ColliderKind = 'solid' | 'hazard' | 'oneway';

export interface Collider extends Aabb {
  kind: ColliderKind;
  objectId: number;
}

/** Collider belonging to trigger-movable groups (kept out of the static grid;
 * each Sim owns a positioned copy updated from its group states). */
export interface GroupedCollider {
  base: Collider;
  groups: number[];
}

/** 45°/22.5° ramp. Surface height at x: see surfaceYAt(). */
export interface SlopeCollider extends Aabb {
  objectId: number;
  dir: 'up' | 'down';
  /** Rise per unit run: 1 (45°) or 0.5 (22.5°). */
  grade: number;
}

/** Walkable surface height of a slope at world x (clamped to its span). */
export function surfaceYAt(s: SlopeCollider, x: number): number {
  const cx = Math.max(s.minX, Math.min(s.maxX, x));
  return s.dir === 'up' ? s.minY + s.grade * (cx - s.minX) : s.minY + s.grade * (s.maxX - cx);
}

/** Interactive (non-solid) trigger zones: portals, pads, orbs, checkpoints,
 * endgate, collectibles. */
export interface Zone extends Aabb {
  objectId: number;
  type: 'portal' | 'pad' | 'orb' | 'checkpoint' | 'endgate' | 'collectible';
  kind: string; // validated per-type kind (see format/types PORTAL/PAD/ORB_KINDS)
  /** Teleport offsets, units (portal kind 'teleport' only). */
  dx?: number;
  dy?: number;
  /** Key item id (collectible kind 'key' only). */
  itemId?: number;
}

/** Solid door that opens (collision removed) once items[itemId] ≥ needed. */
export interface LockDef {
  collider: Collider;
  objectId: number;
  itemId: number;
  needed: number;
}

/** End-of-level margin after the last object, in units. */
const END_MARGIN = 10 * UNITS_PER_BLOCK;

/** Hazard hitboxes shrink relative to the visual (genre convention). */
const SPIKE_HITBOX_SCALE = 0.4;

export class RuntimeLevel {
  readonly data: LevelData;
  readonly colliders: Collider[] = [];
  readonly grid = new StaticGrid<Collider>();
  readonly zones: Zone[] = [];
  readonly zoneGrid = new StaticGrid<Zone>();
  readonly slopes: SlopeCollider[] = [];
  readonly slopeGrid = new StaticGrid<SlopeCollider>();
  /** Colliders in trigger-movable groups (excluded from the static grid). */
  readonly groupedColliders: GroupedCollider[] = [];
  /** Key-lock doors (solid until opened; sim resolves per-attempt state). */
  readonly locks: LockDef[] = [];
  /** Positional + spawn triggers, sorted by x ascending. */
  readonly triggers: TriggerDef[] = [];
  readonly mode: 'normal' | 'platformer';
  /** Editor start-position override (last startpos object in the level). */
  readonly startPos: { x: number; y: number; form?: string; speed?: string } | null;
  /** X coordinate where the level completes. */
  readonly endX: number;
  /** Horizontal speed at level start, units/s. */
  readonly startSpeed: number;

  constructor(data: LevelData) {
    this.data = data;
    this.mode = data.settings.mode;
    let maxX = 0;
    let endgateX: number | null = null;
    let startPos: RuntimeLevel['startPos'] = null;

    for (const obj of data.objects) {
      const trig = triggerFromObject(obj);
      if (trig) {
        this.triggers.push(trig);
        continue;
      }
      if (obj.type === 'startpos') {
        startPos = {
          x: obj.x,
          y: obj.y,
          ...(typeof obj.props?.form === 'string' ? { form: obj.props.form } : {}),
          ...(typeof obj.props?.speed === 'string' ? { speed: obj.props.speed } : {}),
        };
      }
      if (obj.type === 'lock') {
        this.locks.push({
          collider: {
            kind: 'solid',
            objectId: obj.id,
            minX: obj.x - UNITS_PER_BLOCK / 2,
            maxX: obj.x + UNITS_PER_BLOCK / 2,
            minY: obj.y - UNITS_PER_BLOCK / 2,
            maxY: obj.y + UNITS_PER_BLOCK / 2,
          },
          objectId: obj.id,
          itemId: typeof obj.props?.itemId === 'number' ? obj.props.itemId : 1,
          needed: typeof obj.props?.needed === 'number' ? obj.props.needed : 1,
        });
        maxX = Math.max(maxX, obj.x);
        continue;
      }
      const col = colliderFor(obj);
      if (col) {
        this.colliders.push(col);
        if (obj.groups && obj.groups.length > 0) {
          this.groupedColliders.push({ base: col, groups: [...obj.groups] });
        } else {
          this.grid.insert(col, col);
        }
      }
      const slope = slopeFor(obj);
      if (slope) {
        this.slopes.push(slope);
        this.slopeGrid.insert(slope, slope);
      }
      const zone = zoneFor(obj);
      if (zone) {
        this.zones.push(zone);
        this.zoneGrid.insert(zone, zone);
      }
      if (obj.type === 'endgate') endgateX = obj.x;
      maxX = Math.max(maxX, obj.x);
    }

    this.endX = endgateX ?? maxX + END_MARGIN;
    this.startSpeed = SPEED[data.settings.speed];
    this.startPos = startPos;
    this.triggers.sort((a, b) => a.x - b.x || a.id - b.id);
  }
}

function colliderFor(obj: LevelObject): Collider | null {
  const sx = (obj.scaleX ?? 1) * UNITS_PER_BLOCK;
  const sy = (obj.scaleY ?? 1) * UNITS_PER_BLOCK;
  switch (obj.type) {
    case 'block':
      return {
        kind: 'solid',
        objectId: obj.id,
        minX: obj.x - sx / 2,
        maxX: obj.x + sx / 2,
        minY: obj.y - sy / 2,
        maxY: obj.y + sy / 2,
      };
    case 'spike': {
      const hw = (sx / 2) * SPIKE_HITBOX_SCALE;
      const hh = (sy / 2) * SPIKE_HITBOX_SCALE;
      return {
        kind: 'hazard',
        objectId: obj.id,
        minX: obj.x - hw,
        maxX: obj.x + hw,
        minY: obj.y - hh,
        maxY: obj.y + hh,
      };
    }
    case 'oneway':
      // Thin platform: only its top (or bottom, inverted) face interacts.
      return {
        kind: 'oneway',
        objectId: obj.id,
        minX: obj.x - sx / 2,
        maxX: obj.x + sx / 2,
        minY: obj.y + sy / 2 - 6,
        maxY: obj.y + sy / 2,
      };
    case 'endgate':
    case 'decoration':
    case 'portal':
    case 'pad':
    case 'orb':
    case 'slope':
    case 'checkpoint':
    case 'startpos':
    case 'trigger':
    case 'collectible':
    case 'lock': // handled separately in the constructor
    case 'text':
      return null;
  }
}

function slopeFor(obj: LevelObject): SlopeCollider | null {
  if (obj.type !== 'slope') return null;
  const sx = (obj.scaleX ?? 1) * UNITS_PER_BLOCK;
  const grade = typeof obj.props?.grade === 'number' ? obj.props.grade : 1;
  const dir = obj.props?.dir === 'down' ? 'down' : 'up';
  const rise = sx * grade;
  return {
    objectId: obj.id,
    dir,
    grade,
    minX: obj.x - sx / 2,
    maxX: obj.x + sx / 2,
    minY: obj.y - UNITS_PER_BLOCK / 2,
    maxY: obj.y - UNITS_PER_BLOCK / 2 + rise,
  };
}

function zoneFor(obj: LevelObject): Zone | null {
  const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : '';
  switch (obj.type) {
    case 'portal': {
      // Tall gate: 1×3 blocks centered on the object.
      const zone: Zone = {
        type: 'portal',
        kind,
        objectId: obj.id,
        minX: obj.x - UNITS_PER_BLOCK / 2,
        maxX: obj.x + UNITS_PER_BLOCK / 2,
        minY: obj.y - 1.5 * UNITS_PER_BLOCK,
        maxY: obj.y + 1.5 * UNITS_PER_BLOCK,
      };
      if (kind === 'teleport') {
        zone.dx = (typeof obj.props?.dx === 'number' ? obj.props.dx : 0) * UNITS_PER_BLOCK;
        zone.dy = (typeof obj.props?.dy === 'number' ? obj.props.dy : 0) * UNITS_PER_BLOCK;
      }
      return zone;
    }
    case 'collectible':
      return {
        type: 'collectible',
        kind,
        objectId: obj.id,
        minX: obj.x - 18,
        maxX: obj.x + 18,
        minY: obj.y - 18,
        maxY: obj.y + 18,
        itemId: typeof obj.props?.itemId === 'number' ? obj.props.itemId : 1,
      };
    case 'checkpoint':
      return {
        type: 'checkpoint',
        kind: '',
        objectId: obj.id,
        minX: obj.x - UNITS_PER_BLOCK / 2,
        maxX: obj.x + UNITS_PER_BLOCK / 2,
        minY: obj.y - 1.5 * UNITS_PER_BLOCK,
        maxY: obj.y + 1.5 * UNITS_PER_BLOCK,
      };
    case 'endgate':
      return {
        type: 'endgate',
        kind: '',
        objectId: obj.id,
        minX: obj.x - UNITS_PER_BLOCK / 2,
        maxX: obj.x + UNITS_PER_BLOCK / 2,
        minY: obj.y - 2.5 * UNITS_PER_BLOCK,
        maxY: obj.y + 2.5 * UNITS_PER_BLOCK,
      };
    case 'pad':
      // Thin strip on the floor of its cell.
      return {
        type: 'pad',
        kind,
        objectId: obj.id,
        minX: obj.x - UNITS_PER_BLOCK / 2,
        maxX: obj.x + UNITS_PER_BLOCK / 2,
        minY: obj.y - UNITS_PER_BLOCK / 2,
        maxY: obj.y - UNITS_PER_BLOCK / 2 + 10,
      };
    case 'orb':
      // Generous tap zone around the orb.
      return {
        type: 'orb',
        kind,
        objectId: obj.id,
        minX: obj.x - 21,
        maxX: obj.x + 21,
        minY: obj.y - 21,
        maxY: obj.y + 21,
      };
    default:
      return null;
  }
}
